import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    timeout: 30000,
});

// Simple in-memory cache for API responses
const cache = new Map();
const CACHE_DURATION = 60000; // 1 minute cache

const getCached = (key) => {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.data;
    }
    return null;
};

const setCache = (key, data) => {
    cache.set(key, { data, timestamp: Date.now() });
};

// Add auth token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Crypto API with caching
export const cryptoApi = {
    getTopCoins: async (limit = 20) => {
        const cacheKey = `top-coins-${limit}`;
        const cached = getCached(cacheKey);
        if (cached) return { data: cached };
        
        const response = await api.get(`/crypto/top-coins?limit=${limit}`);
        setCache(cacheKey, response.data);
        return response;
    },
    getCoinPrice: async (coinId) => {
        const cacheKey = `price-${coinId}`;
        const cached = getCached(cacheKey);
        if (cached) return { data: cached };
        
        const response = await api.get(`/crypto/price/${coinId}`);
        setCache(cacheKey, response.data);
        return response;
    },
    getHistoricalData: async (coinId, days = 7) => {
        const cacheKey = `history-${coinId}-${days}`;
        const cached = getCached(cacheKey);
        if (cached) return { data: cached };
        
        const response = await api.get(`/crypto/historical/${coinId}?days=${days}`);
        setCache(cacheKey, response.data);
        return response;
    },
    getCoinDetails: async (coinId) => {
        const cacheKey = `coin-${coinId}`;
        const cached = getCached(cacheKey);
        if (cached) return { data: cached };
        
        const response = await api.get(`/crypto/coin/${coinId}`);
        setCache(cacheKey, response.data);
        return response;
    },
    searchCoins: (query) => api.get(`/crypto/search?query=${query}`),
    clearCache: () => cache.clear(),
};

// Portfolio API
export const portfolioApi = {
    getPortfolio: () => api.get('/portfolio'),
    addHolding: (holding) => api.post('/portfolio', holding),
    removeHolding: (holdingId) => api.delete(`/portfolio/${holdingId}`),
};

// Watchlist API
export const watchlistApi = {
    getWatchlist: () => api.get('/watchlist'),
    addToWatchlist: (item) => api.post('/watchlist', item),
    removeFromWatchlist: (itemId) => api.delete(`/watchlist/${itemId}`),
};

export default api;
